import {
  CopyIcon,
  DownloadIcon,
  ImportIcon,
  MoreHorizontalIcon,
  PlusIcon,
} from '@blocksuite/icons/lit';
import { addDays } from 'date-fns/addDays';
import { getISOWeek } from 'date-fns/getISOWeek';
import { startOfWeek } from 'date-fns/startOfWeek';
import { html, nothing, type TemplateResult } from 'lit';

import {
  dataViewI18nT,
  getDataViewRuntimeLocale,
} from '../../../../core/i18n.js';
import {
  renderTemporalModeGroup,
  renderTemporalRangeControls,
  TEMPORAL_TOOLBAR_ICON_SIZE,
} from '../../../shared/toolbar/temporal-toolbar.js';
import type { CalendarMode } from '../../define.js';
import { styles } from '../calendar-view-styles.js';

export function renderCalendarToolbar({
  hasOutboundMailboxLink = false,
  mailboxLinkUnavailable = false,
  mode,
  readonly,
  modeControlsDisabled = readonly,
  onCopyFeed = () => {},
  onCreate = () => {},
  onExport = () => {},
  onImportIcs = () => {},
  onModeChange,
  onNext,
  onPrevious,
  onToday,
  onToggleFeedForm = () => {},
  rangeTitle,
  showDataActions = true,
  showBottomBorder = true,
}: {
  hasOutboundMailboxLink?: boolean;
  mailboxLinkUnavailable?: boolean;
  mode: CalendarMode;
  modeControlsDisabled?: boolean;
  onCopyFeed?: () => void;
  onCreate?: () => void;
  onExport?: () => void;
  onImportIcs?: (change: Event) => void;
  onModeChange: (mode: CalendarMode) => void;
  onNext: () => void;
  onPrevious: () => void;
  onToday: () => void;
  onToggleFeedForm?: () => void;
  rangeTitle: string;
  readonly: boolean;
  showDataActions?: boolean;
  showBottomBorder?: boolean;
}): TemplateResult {
  const modeLabel = (item: CalendarMode) =>
    dataViewI18nT(`calendar.mode.${item}`);
  return html`<div
    class="${styles.toolbar}"
    data-bottom-border=${showBottomBorder ? 'true' : 'false'}
  >
    ${renderTemporalModeGroup<CalendarMode>({
      activeMode: mode,
      buttonClass: styles.modeButton,
      groupClass: styles.modeGroup,
      onModeChange,
      options: (['day', 'week', 'month'] as const).map(item => ({
        ariaLabel: dataViewI18nT('calendar.accessibility.switchMode', {
          mode: modeLabel(item),
        }),
        disabled: modeControlsDisabled,
        key: item,
        label: modeLabel(item),
      })),
    })}
    ${renderTemporalRangeControls({
      buttonClass: styles.todayButton,
      groupClass: styles.navGroup,
      iconButtonClass: styles.iconButton,
      nextLabel: dataViewI18nT('calendar.accessibility.nextRange'),
      onNext,
      onPrevious,
      onToday,
      previousLabel: dataViewI18nT('calendar.accessibility.previousRange'),
      rangeTitle,
      rangeTitleClass: styles.rangeTitle,
      todayAriaLabel: dataViewI18nT('calendar.accessibility.goToday'),
      todayLabel: dataViewI18nT('calendar.action.today'),
    })}
    ${showDataActions
      ? html`<div class="${styles.actionGroup}">
          <button
            class="${styles.primaryButton}"
            aria-label=${dataViewI18nT('calendar.accessibility.createEvent')}
            ?disabled=${readonly}
            @click=${onCreate}
          >
            ${PlusIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
            <span>${dataViewI18nT('calendar.action.new')}</span>
          </button>
          <details class="${styles.moreMenu}">
            <summary
              class="${styles.todayButton}"
              aria-label=${dataViewI18nT('calendar.accessibility.moreActions')}
            >
              ${MoreHorizontalIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
              <span>${dataViewI18nT('calendar.action.more')}</span>
            </summary>
            <div class="${styles.menuPanel}">
              <button
                class="${styles.todayButton}"
                aria-label=${dataViewI18nT(
                  'calendar.accessibility.exportVisible'
                )}
                @click=${onExport}
              >
                ${DownloadIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
                <span>${dataViewI18nT('calendar.action.export')}</span>
              </button>
              <button
                class="${styles.todayButton}"
                aria-label=${dataViewI18nT('calendar.accessibility.copyFeed')}
                ?disabled=${readonly}
                @click=${readonly ? undefined : onCopyFeed}
              >
                ${CopyIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
                <span>${dataViewI18nT('calendar.action.copyFeed')}</span>
              </button>
              <button
                class="${styles.todayButton}"
                aria-label=${dataViewI18nT('calendar.accessibility.addFeed')}
                ?disabled=${readonly}
                @click=${onToggleFeedForm}
              >
                ${PlusIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
                <span>${dataViewI18nT('calendar.action.addFeed')}</span>
              </button>
              <div class="${styles.menuField}">
                <span>${dataViewI18nT('calendar.mailboxLink.title')}</span>
                <span class="${styles.mailAccountOption}">
                  ${mailboxLinkUnavailable
                    ? dataViewI18nT('calendar.mailboxLink.unavailable')
                    : hasOutboundMailboxLink
                      ? dataViewI18nT('calendar.mailboxLink.outboundLinked')
                      : dataViewI18nT('calendar.mailboxLink.notLinked')}
                </span>
              </div>
              <label
                class="${styles.todayButton}"
                data-disabled=${readonly}
                aria-label=${dataViewI18nT('calendar.accessibility.importIcs')}
              >
                ${ImportIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
                <span>${dataViewI18nT('calendar.action.import')}</span>
                <input
                  class="${styles.fileInput}"
                  type="file"
                  accept=".ics,text/calendar"
                  ?disabled=${readonly}
                  @change=${readonly ? undefined : onImportIcs}
                />
              </label>
            </div>
          </details>
        </div>`
      : nothing}
  </div>`;
}

export function renderCalendarIcsFeedForm({
  onCancel,
  onInput,
  onSubmit,
  open,
  readonly,
  url,
}: {
  onCancel: () => void;
  onInput: (value: string) => void;
  onSubmit: (url: string) => void;
  open: boolean;
  readonly: boolean;
  url: string;
}): TemplateResult | typeof nothing {
  if (!open) return nothing;
  return html`<form
    class="${styles.feedForm}"
    @submit=${(event: SubmitEvent) => {
      event.preventDefault();
      onSubmit(url);
    }}
    @keydown=${(event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    }}
  >
    <label class="${styles.feedField}">
      <span>${dataViewI18nT('calendar.toolbar.feedUrl')}</span>
      <input
        type="url"
        inputmode="url"
        placeholder="https://example.com/calendar.ics"
        .value=${url}
        ?disabled=${readonly}
        @input=${(event: InputEvent) =>
          onInput((event.currentTarget as HTMLInputElement).value)}
      />
    </label>
    <button class="${styles.primaryButton}" type="submit" ?disabled=${readonly}>
      ${PlusIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
      <span>${dataViewI18nT('calendar.action.add')}</span>
    </button>
    <button class="${styles.todayButton}" type="button" @click=${onCancel}>
      ${dataViewI18nT('calendar.action.cancel')}
    </button>
  </form>`;
}

export function renderCalendarRangeTitle(
  mode: CalendarMode,
  cursor: Date
): string {
  if (mode === 'day') {
    return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
      year: 'numeric',
    }).format(cursor);
  }
  if (mode === 'week') {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    const end = addDays(start, 6);
    const formatter = new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const range =
      typeof formatter.formatRange === 'function'
        ? formatter.formatRange(start, end)
        : `${formatter.format(start)} - ${formatter.format(end)}`;
    return `${range} (KW ${getISOWeek(start)})`;
  }
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    month: 'long',
    year: 'numeric',
  }).format(cursor);
}
